import { formatTime } from '../services/formatTime';

export type Contract = {
  contractId: number;
  contractNo: number;
  calcStatus: CalcStatus;
  calcValidDate?: string | null;
  startDate: string;
  endDate?: string;
  collectionConstraintTypeId: number;
  collectionConstraintWeekDays: Weekday[];
  specificTimeStartAt?: string;
  specificTimeEndAt?: string;
  supplierCompany?: {
    id: number;
    name: string;
  } | null;
  contractorCompany?: {
    id: number;
    name: string;
  } | null;
  wastePointCompany?: {
    id: number;
    name: string;
  } | null;
  goesStraight: 0 | 1;
  wasteItems: {
    id: number;
    name: string;
  }[];
};
export type CalcStatus =
  | 'uncalced'
  | 'completed'
  | 'incomplete'
  | 'partComplete'
  | 'updateContract';

export type CompanyInfo = {
  companyId: number | null;
  companyPicName: string;
  phoneNumber: string;
};

export type WasteItemsInfo = {
  contractDetailId?: number | null;
  wasteItemId: number | null;
  wasteItemName: string;
  amountOfWaste: number | null;
  disposalSiteCompanyId: number | null;
  specificTimeStartAt?: string | null;
  specificTimeEndAt?: string | null;
};

export type ContractFormData = {
  contractNo: string;
  receivedAt: string;
  updatedBy: string;
  salesPic: {
    id: number;
    name: string;
  } | null;
  contractDocumentTypeId: number;
  supplierCompanyInfo: CompanyInfo;
  contractorCompanyInfo: CompanyInfo | null;
  wastePointCompanyInfo: CompanyInfo | null;
  wastePointCompanyIsTheSameSupplier: boolean;
  carCategoryIds: number[];
  collectionConstraintTypeId: number | null;
  collectionConstraintWeekDays: number[];
  collectionConstraintDateStart: string;
  collectionConstraintDateEnd: string;
  collectionConstraintTimeStart: string;
  collectionConstraintTimeEnd: string;
  collectionEstimatedTime: number | null;
  goesStraight: boolean;
  requiredSkillLevelId: number | null;
  requiredQualificationIds: number[];
  memo: string;
  wasteItemsInfo: WasteItemsInfo[];
};

export type CompanyInfoDetail = {
  companyId: number | null;
  companyName: string;
  postCode: string;
  prefecture: string;
  companyPicName: string;
  phoneNumber: string;
  address1: string;
  address2: string;
  address3: string;
};
export type WasteItemsInfoInDetail = {
  contractDetailId: number;
  wasteItemId: number | null;
  wasteItemName: string;
  amountOfWaste: number | null;
  disposalSiteCompany?: {
    id: number;
    name: string;
  } | null;
  specificTimeStartAt?: string | null;
  specificTimeEndAt?: string | null;
};

export type ContractDetail = {
  contractId: number;
  contractNo: number;
  receivedAt: string;
  salesPic?: {
    id: number;
    name: string;
  } | null;
  updatedBy?: {
    id: number;
    name: string;
  } | null;
  contractDocumentType: {
    id: number;
    name: string;
  };
  supplierCompanyInfo?: CompanyInfoDetail | null;
  contractorCompanyInfo?: CompanyInfoDetail | null;
  wastePointCompanyInfo?: CompanyInfoDetail | null;
  carCategories?:
    | {
        id: number;
        name: string;
      }[]
    | null;
  collectionConstraintType: {
    id: number;
    name: string;
  };
  collectionConstraintWeekDays?: Weekday[] | null;
  collectionConstraintDateStart: string;
  collectionConstraintDateEnd?: string | null;
  collectionConstraintTimeStart?: string | null;
  collectionConstraintTimeEnd?: string | null;
  collectionEstimatedTime: number;
  goesStraight: boolean;
  requiredSkillLevel: {
    id: number | null;
    name: string;
  };
  requiredQualifications: {
    id: number;
    name: string;
  }[];
  memo?: string | null;
  wasteItemsInfo?: WasteItemsInfoInDetail[] | null;
  isEditable?: boolean;
};
export const transformContractDetailToFormData = (
  detail: ContractDetail
): ContractFormData => {
  return {
    contractNo: `${detail.contractNo}`, // 表示用パラメータなので stringify
    receivedAt: detail.receivedAt,
    updatedBy: detail.updatedBy?.name ?? '',
    salesPic: detail.salesPic ?? null,
    contractDocumentTypeId: detail.contractDocumentType.id,
    supplierCompanyInfo: detail.supplierCompanyInfo
      ? {
          companyId: detail.supplierCompanyInfo.companyId,
          companyPicName: detail.supplierCompanyInfo.companyPicName,
          phoneNumber: detail.supplierCompanyInfo.phoneNumber,
        }
      : {
          companyId: null,
          companyPicName: '',
          phoneNumber: '',
        },
    contractorCompanyInfo: detail.contractorCompanyInfo
      ? {
          companyId: detail.contractorCompanyInfo.companyId,
          companyPicName: detail.contractorCompanyInfo.companyPicName,
          phoneNumber: detail.contractorCompanyInfo.phoneNumber,
        }
      : null,
    wastePointCompanyInfo: detail.wastePointCompanyInfo
      ? {
          companyId: detail.wastePointCompanyInfo.companyId,
          companyPicName: detail.wastePointCompanyInfo.companyPicName,
          phoneNumber: detail.wastePointCompanyInfo.phoneNumber,
        }
      : null,
    // バックエンドで「排出事業者と同じ」の表現方法が特殊になっている関係から、
    // 初期状態で排出事業者と排出事業場が同じ場合はチェックが入っている状態とする
    // @see https://fenrir-inc.backlog.com/view/MRBN_UR_INHOUSE-456#comment-318502794
    // @see https://fenrir-inc.backlog.com/view/MRBN_UR_INHOUSE-456#comment-319010278
    wastePointCompanyIsTheSameSupplier: Boolean(
      detail.wastePointCompanyInfo &&
        detail.supplierCompanyInfo &&
        detail.wastePointCompanyInfo.companyId ===
          detail.supplierCompanyInfo.companyId &&
        detail.wastePointCompanyInfo.companyPicName ===
          detail.supplierCompanyInfo.companyPicName &&
        detail.wastePointCompanyInfo.phoneNumber ===
          detail.supplierCompanyInfo.phoneNumber
    ),
    carCategoryIds: detail.carCategories?.map((c) => c.id) ?? [],
    collectionConstraintTypeId: detail.collectionConstraintType.id,
    collectionConstraintWeekDays:
      detail.collectionConstraintWeekDays?.map(convertWeekdayToId) ?? [],
    collectionConstraintDateStart: detail.collectionConstraintDateStart,
    collectionConstraintDateEnd: detail.collectionConstraintDateEnd ?? '',
    collectionConstraintTimeStart: detail.collectionConstraintTimeStart
      ? formatTime(detail.collectionConstraintTimeStart)
      : '',
    collectionConstraintTimeEnd: detail.collectionConstraintTimeEnd
      ? formatTime(detail.collectionConstraintTimeEnd)
      : '',
    collectionEstimatedTime: detail.collectionEstimatedTime,
    goesStraight: detail.goesStraight,
    requiredSkillLevelId: detail.requiredSkillLevel
      ? detail.requiredSkillLevel.id
      : null,
    requiredQualificationIds: detail.requiredQualifications.map((q) => q.id),
    memo: detail.memo ?? '',
    wasteItemsInfo:
      detail.wasteItemsInfo?.map((info) => ({
        ...info,
        specificTimeStartAt: info.specificTimeStartAt
          ? formatTime(info.specificTimeStartAt)
          : '',
        specificTimeEndAt: info.specificTimeEndAt
          ? formatTime(info.specificTimeEndAt)
          : '',
        disposalSiteCompanyId: info.disposalSiteCompany?.id ?? null,
      })) ?? [],
  };
};

export type ContractDocumentType = {
  contractDocumentTypeId: string;
  contractDocumentType: string;
};
export type CollectionConstraintType = {
  collectionConstraintTypeId: number;
  collectionConstraintType: string;
};

export type Weekday = '月' | '火' | '水' | '木' | '金' | '土' | '日';
const convertWeekdayToId = (weekday: Weekday) => {
  switch (weekday) {
    case '月':
      return 1;
    case '火':
      return 2;
    case '水':
      return 3;
    case '木':
      return 4;
    case '金':
      return 5;
    case '土':
      return 6;
    case '日':
      return 7;
  }
};

export type GetContractCondition = {
  contractNo?: number | null;
  startDate?: string;
  endDate?: string;
  companyName?: string;
  calcStatus: CalcStatusRequest;
  goesStraight?: number | null;
  wasteItemIds?: number[];
  offset?: number;
  page?: number;
  limit?: number;
};
export type CalcStatusRequest = 'uncalced' | 'completed' | 'incomplete' | 'all';
export type GetContractResponse = {
  lastCalculatedAt: string;
  total: number;
  contracts: Contract[];
};
